import { Color, Spacing } from '@persona-id/design-tokens';
import { sortBy, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import ContextMenu from 'dashboard/components/ContextMenu';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import Highlighter from 'dashboard/components/ReportResult/Highlighter';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import { MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import Tooltip from 'dashboard/components/Tooltip';
import AccordionInline from 'dashboard/components-lib/AccordionInline';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { Report } from 'dashboard/models/report';
import {
  AdverseMediaIgnoreListEntry,
  AdverseMediaType,
  adverseMediaTypeDisplayText,
  adverseMediaTypeTooltipText,
  MatchLevel,
  matchLevelDisplayText,
  Media,
  RelatedSource,
} from 'dashboard/models/report/adverse-media';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { paragraphMixins } from 'design-system/mixins/text';
import { FontWeight } from 'design-system/variables/text';
import { COUNTRIES } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 5;
const MAX_AGE = 120;
const BIRTHDATES_SEP = ', ';

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const Stickers = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const StyledStickerContainer = styled('span')`
  margin-right: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
`;

const NotApplicableText = styled('span')`
  color: ${Color.CoreSlate1000};
`;

const MediumBlock = styled('div')`
  display: flex;
  flex-direction: column;
  padding-bottom: ${Spacing[2]};
  margin-bottom: ${Spacing[4]};

  &:not(:last-of-type) {
    border-bottom: 1px solid ${COLORS.gray20};
  }
`;

const MediumTitle = styled('div')`
  ${paragraphMixins.lgBold};
  line-height: 26px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const MediumBody = styled('div')`
  color: ${COLORS.gray100};
`;

const Subtext = styled('div')`
  ${paragraphMixins.md};
  display: flex;
  color: ${COLORS.gray70};
`;

const SubtextDate = styled('div')`
  flex-shrink: 0;
`;

const SubtextUrl = styled.a`
  ${paragraphMixins.md};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${COLORS.gray70};
`;

const StyledTooltip = styled(Tooltip).attrs(() => ({ placement: 'top', trigger: 'hover' }))`
  max-width: 400px;
`;

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

const ProfileInfo = ({
  relatedSource,
  isRedacted,
  isIgnoredEntity,
  report,
  multiCardFormat,
  isBusiness,
  isSnapshot,
}: {
  relatedSource: RelatedSource;
  isRedacted: boolean;
  isIgnoredEntity?: boolean;
  report: Report;
  multiCardFormat: boolean;
  isBusiness: boolean;
  isSnapshot: boolean;
}) => {
  const { birthdates } = relatedSource;
  const aliases = uniq(relatedSource.akas);
  const [ignoreEntityModal, { enable: showIgnoreEntityModal, disable: hideIgnoreEntityModal }] =
    useToggle(false);

  let ageText;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (birthdates?.length > 0) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const age = Math.floor(DateTime.local().diff(DateTime.fromISO(birthdates[0]), 'years').years);
    if (age <= MAX_AGE) {
      ageText = age;
    }
  }

  let profileComponent;
  if (ageText) {
    profileComponent = (
      <>
        <AttributeGrid colCount={3}>
          <AttributeItem
            title='Name'
            value={relatedSource.name}
            toggleable={false}
            wordBreak='break-word'
            isRedacted={isRedacted}
            dataTest='report-adverse-media__name'
          />
          <AttributeItem
            title='Age'
            value={ageText}
            toggleable={false}
            wordBreak='break-word'
            isRedacted={isRedacted}
            dataTest='report-adverse-media__age'
          />
          <AttributeItem
            title='Birthday'
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            value={birthdates.join(BIRTHDATES_SEP)}
            toggleable={false}
            wordBreak='break-word'
            isRedacted={isRedacted}
            dataTest='report-adverse-media__age'
          />
          <AttributeItem
            title='Relevance'
            value={
              relatedSource['match-types']
                ? relatedSource['match-types'].map((t) => matchLevelDisplayText(t as MatchLevel))
                : ''
            }
            toggleable={false}
            wordBreak='break-word'
            isRedacted={isRedacted}
            dataTest='report-adverse-media__match_types'
          />
        </AttributeGrid>
      </>
    );
  } else {
    profileComponent = (
      <AttributeGrid colCount={2}>
        <AttributeItem
          title='Name'
          value={relatedSource.name}
          toggleable={false}
          wordBreak='break-word'
          isRedacted={isRedacted}
          dataTest='report-adverse-media__name'
        />
        <AttributeItem
          title='Relevance'
          value={
            relatedSource['match-types']
              ? relatedSource['match-types'].map((t) => matchLevelDisplayText(t as MatchLevel))
              : ''
          }
          toggleable={false}
          wordBreak='break-word'
          isRedacted={isRedacted}
          dataTest='report-adverse-media__match_types'
        />
      </AttributeGrid>
    );
  }

  return (
    <>
      <TitleContainer>
        <Card.Title>
          Profile <Spacer width={1} />
          {isIgnoredEntity && <Sticker variant='danger'>DISMISSED</Sticker>}
        </Card.Title>
        {multiCardFormat && !isBusiness && !isSnapshot && !isIgnoredEntity && (
          <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
            <ContextMenu
              variant='inverse'
              items={[
                {
                  id: 'reports-ignore-hits-modal__dismiss-hits-button',
                  onClick: showIgnoreEntityModal,
                  hidden: false,
                  label: 'Dismiss identity match',
                },
              ]}
              renderItem={(item) => (
                <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                  {item.label}
                </ContextMenu.Item>
              )}
            />
            <IgnoreHitsModal
              ignoreHitsModalOpen={ignoreEntityModal}
              hideIgnoreHitsModal={hideIgnoreEntityModal}
              modalHeaderText='Dismiss Identity Match?'
              modalDescriptionText='Dismissing an identity match will prevent all media for this identity from appearing in future report runs.'
              report={report}
              entityId={relatedSource.id}
              ignoreType='entity'
            />
          </Permissioned>
        )}
      </TitleContainer>
      <Spacer height={1} />

      {profileComponent}
      <Spacer height={1} />

      <AccordionInline title={`Aliases (${aliases.length})`} defaultExpanded={false}>
        {aliases.map((alias) => (
          <div key={alias}>{alias}</div>
        ))}
      </AccordionInline>
    </>
  );
};

const Categories = ({ relatedSource }: { relatedSource: RelatedSource; isRedacted: boolean }) => {
  const adverseMediaCategories = uniq(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    relatedSource.sources
      .flatMap((hit) => hit['types'])
      .map((category) => ({
        displayText: adverseMediaTypeDisplayText(category as AdverseMediaType),
        tooltipText: adverseMediaTypeTooltipText(category as AdverseMediaType),
      })),
  );

  return (
    <>
      <Card.Title>Categories</Card.Title>
      <Stickers>
        {adverseMediaCategories.map((category, index) => {
          let stickerComponent = (
            <Sticker key={category.displayText}>{category.displayText}</Sticker>
          );
          if (category.tooltipText) {
            stickerComponent = (
              <StyledTooltip tooltip={category.tooltipText}>{stickerComponent}</StyledTooltip>
            );
          }
          return <StyledStickerContainer key={index}>{stickerComponent}</StyledStickerContainer>;
        })}
      </Stickers>
    </>
  );
};

const Countries = ({ relatedSource }: { relatedSource: RelatedSource; isRedacted: boolean }) => {
  const countries = uniq(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    relatedSource.sources.flatMap((hit) => hit['country-codes']).map((code) => COUNTRIES[code]),
  );

  return (
    <>
      <Card.Title>Countries</Card.Title>
      <Stickers>
        {countries.length > 0 ? (
          countries.map((country) => (
            <StyledStickerContainer key={country}>
              <Sticker key={country}>{country}</Sticker>
            </StyledStickerContainer>
          ))
        ) : (
          <NotApplicableText>N/A</NotApplicableText>
        )}
      </Stickers>
    </>
  );
};

const MediaArticles = ({
  relatedSource,
  ignoreListMedia,
  isIgnoredEntity,
  report,
  multiCardFormat,
  isBusiness,
  isSnapshot,
}: {
  relatedSource: RelatedSource;
  isRedacted: boolean;
  ignoreListMedia?: AdverseMediaIgnoreListEntry[];
  isIgnoredEntity?: boolean;
  report: Report;
  multiCardFormat: boolean;
  isBusiness: boolean;
  isSnapshot: boolean;
}) => {
  const [isActiveTabSelected, setIsActiveTabSelected] = useState(true);
  const [ignoreMediaModal, { enable: showIgnoreMediaModal, disable: hideIgnoreMediaModal }] =
    useToggle(false);

  const names = [relatedSource.name];
  const akas = uniq(relatedSource.akas);

  // Hide ignored media if the entity is ignored
  const ignoredMedia = isIgnoredEntity
    ? []
    : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ignoreListMedia.map((entry) => entry.match).flatMap((match) => match.media);

  const uniqueMedia = isActiveTabSelected ? uniq(relatedSource.media) : uniq(ignoredMedia);
  const sortedMedia = sortBy(['date'], uniqueMedia)
    .reverse()
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    .filter((media) => media.date);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const noDateMedia = uniqueMedia.filter((media) => media.date == null);
  const media = [...sortedMedia, ...noDateMedia];

  return (
    <>
      <TitleContainer>
        <Card.Title>
          Adverse Media <Spacer width={1} />
          {ignoredMedia.length === 0 && <Sticker>{media.length}</Sticker>}
        </Card.Title>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {relatedSource.media.length > 0 && multiCardFormat && !isBusiness && !isSnapshot && (
          <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
            <ContextMenu
              variant='inverse'
              items={[
                {
                  id: 'reports-ignore-hits-modal__dismiss-hits-button',
                  onClick: showIgnoreMediaModal,
                  hidden: false,
                  label: 'Dismiss media matches',
                },
              ]}
              renderItem={(item) => (
                <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                  {item.label}
                </ContextMenu.Item>
              )}
            />
            <IgnoreHitsModal
              ignoreHitsModalOpen={ignoreMediaModal}
              hideIgnoreHitsModal={hideIgnoreMediaModal}
              report={report}
              entityId={relatedSource.id}
              ignoreType='media'
            />
          </Permissioned>
        )}
      </TitleContainer>
      {ignoredMedia.length > 0 && (
        <>
          <TabBar>
            <Tab
              id='report-adverse-media__media-tab-active'
              selected={isActiveTabSelected}
              onClick={() => {
                setIsActiveTabSelected(true);
              }}
            >
              <TabText>
                <span>Active</span>
                <Sticker>{uniq(relatedSource.media).length}</Sticker>
              </TabText>
            </Tab>
            <Tab
              id='report-adverse-media__media-tab-ignored'
              selected={!isActiveTabSelected}
              onClick={() => {
                setIsActiveTabSelected(false);
              }}
            >
              <TabText>
                <span>Dismissed</span>
                <Sticker>{uniq(ignoredMedia).length}</Sticker>
              </TabText>
            </Tab>
          </TabBar>
          <Spacer height={2} />
        </>
      )}

      {media.length === 0 ? (
        <MatchIconPositive label={`No ${isActiveTabSelected ? 'active' : 'dismissed'} matches`} />
      ) : (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <AdverseMediaMediaList media={media} names={names} akas={akas} />
      )}
    </>
  );
};

export const AdverseMediaMediaList = ({
  media,
  names,
  akas,
}: {
  media: Media[];
  names: string[];
  akas: string[];
}) => {
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const mediaPage = media.slice((currentPage - 1) * perPage, (currentPage - 1) * perPage + perPage);

  return (
    <>
      {mediaPage.map((medium: Media) => (
        <MediumBlock key={medium.url}>
          <MediumTitle>
            <Highlighter exactMatchWords={names} partialMatchWords={akas}>
              {medium.title}
            </Highlighter>
          </MediumTitle>
          <Spacer height={1} />

          <MediumBody>
            <Highlighter exactMatchWords={names} partialMatchWords={akas}>
              {medium.snippet}
            </Highlighter>
          </MediumBody>
          <Spacer height={1} />

          <Subtext color='secondary'>
            {medium.date && <SubtextDate>{formatDate(medium.date)} ·</SubtextDate>}
            <Spacer width={0.5} height={0} />
            <SubtextUrl href={medium.url} target='_blank' rel='noreferrer'>
              {medium.url}
            </SubtextUrl>
          </Subtext>
          <Spacer height={1} />
        </MediumBlock>
      ))}

      {media.length > DEFAULT_PER_PAGE && (
        <Pagination
          pageSize={perPage}
          paginationParams={{
            'current-page': currentPage,
            'total-count': media.length,
            'total-pages': Math.ceil(media.length / perPage),
          }}
          handlePageSizeClick={setPerPage}
          handlePageLinkClick={setCurrentPage}
        />
      )}
    </>
  );
};

interface AdverseMediaIdentityMatchDetailsProps {
  relatedSource: RelatedSource;
  multiCardFormat: boolean;
  /**
   * @deprecated - should handle redaction upstream
   */
  isRedacted?: boolean;
  ignoreListMedia?: AdverseMediaIgnoreListEntry[] | [];
  isIgnoredEntity?: boolean;
  report?: Report;
  isBusiness?: boolean;
}

const AdverseMediaIdentityMatchDetails = ({
  relatedSource,
  multiCardFormat,
  /**
   * @deprecated - should handle redaction upstream
   */
  isRedacted = false,
  ignoreListMedia = [],
  isIgnoredEntity = false,
  report,
  isBusiness = false,
}: AdverseMediaIdentityMatchDetailsProps) => {
  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <ProfileInfo
        relatedSource={relatedSource}
        isRedacted={isRedacted}
        isIgnoredEntity={isIgnoredEntity}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        report={report}
        multiCardFormat={multiCardFormat}
        isBusiness={isBusiness}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isSnapshot={report.type === ApiModel.ReportSnapshotAdverseMedia}
      />
      <Countries relatedSource={relatedSource} isRedacted={isRedacted} />
      <Categories relatedSource={relatedSource} isRedacted={isRedacted} />
      <MediaArticles
        relatedSource={relatedSource}
        isRedacted={isRedacted}
        ignoreListMedia={ignoreListMedia}
        isIgnoredEntity={isIgnoredEntity}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        report={report}
        multiCardFormat={multiCardFormat}
        isBusiness={isBusiness}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isSnapshot={report.type === ApiModel.ReportSnapshotAdverseMedia}
      />
    </ReportResultLayout>
  );
};

export default AdverseMediaIdentityMatchDetails;
